import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

const normalizeText = str =>
  str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// CHECKBOX-CLONE-01: Componente local que replica visualmente el
// TriCheckbox del SelectMultipleFilterTree (filtro Sector) sin
// depender del FieldCheckbox global. El FieldCheckbox global pinta
// la casilla con interior emerald-950 y el texto en blanco en
// hover/focus/checked \u2014 ambos hacen los labels invisibles sobre el
// sidebar claro del modo Idealista.
//
// Mantenemos <Field type="checkbox"> de react-final-form igual que
// FieldCheckbox por dentro, as\u00ed la integraci\u00f3n con form state
// (query params, marcado/desmarcado, reset) es id\u00e9ntica.
const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M2.5 6.2l2.4 2.3 4.6-4.6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CustomCheckboxOption = ({ id, name, value, label }) => {
  // Workaround Firefox/RFF que tambi\u00e9n vive en FieldCheckbox global.
  const handleOnChange = (input, event) => {
    const { onBlur, onChange } = input;
    onChange(event);
    onBlur(event);
  };
  return (
    <Field type="checkbox" name={name} value={value}>
      {({ input }) => (
        <label htmlFor={id} className={css.optionRow}>
          <input
            id={id}
            type="checkbox"
            {...input}
            className={css.hiddenInput}
            onChange={event => handleOnChange(input, event)}
          />
          <span
            className={classNames(css.checkbox, {
              [css.checkboxChecked]: input.checked,
            })}
            aria-hidden="true"
          >
            {input.checked ? <CheckIcon /> : null}
          </span>
          <span className={css.optionText}>{label}</span>
        </label>
      )}
    </Field>
  );
};

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, legend, searchable, searchPlaceholder } = props;
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!searchable || !query) return options;
    const q = normalizeText(query);
    return options.filter(o => normalizeText(o.label).includes(q));
  }, [options, query, searchable]);

  return (
    <fieldset className={className}>
      {legend ? <legend className={css.accessibilityLegend}>{legend}</legend> : null}
      {searchable ? (
        <input
          type="text"
          className={css.searchInput}
          placeholder={searchPlaceholder || 'Buscar…'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
          autoComplete="off"
        />
      ) : null}
      <ul className={css.list}>
        {filtered.map(optionConfig => {
          const { option, label } = optionConfig;
          const fieldId = `${id}.${option}`;
          return (
            <li key={fieldId} className={css.item}>
              <CustomCheckboxOption id={fieldId} name={name} label={label} value={option} />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Function} props.getAriaLabel - The function to retrieve the aria label for the component
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    searchable,
    searchPlaceholder,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  // FILTER-ACTIVE-INDICATOR: sin conteo numérico junto al label. El estado
  // "filtro activo" se indica visualmente: en popup, PopupOpenerButton pinta
  // el botón con la clase labelSelected (isSelected); en plain, FilterPlain
  // resalta el label vía la misma prop isSelected.
  const labelForPopup = label;

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={labelForPopup}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        legend={label}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        legend={label}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
